//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import * as Rx from 'rxjs';

/* eslint no-unused-vars: 'off' */ // excuse for module vars
/* eslint import/no-unresolved: 'off' */
import 'app/curalate.store';

const curalate = angular.module('curalate', [
  'curalate.constants',
  'curalate.store',
  'curalate.tracking',
  'angularFileUpload',
  'infinite-scroll',
  'mentio',
  'ngAnimate',
  'ngCookies',
  'ngMessages',
  'ngTable',
  'nsPopover',
  'pasvaz.bindonce',
  'toastr',
  'ui.bootstrap',
  'ui.router',
  'LocalStorageModule',
  'angular-uuid',
  'unison',
  'vs-repeat',
  'highcharts-ng',
]);

const uia = angular.module('uia', ['curalate', 'unison']);

const toasts = angular.module('toasts', ['curalate']);

angular.module('curalate.igAdsReporting', ['curalate']);

uia.config([
  '$locationProvider',
  function ($locationProvider) {
    $locationProvider.html5Mode({
      enabled: true,
      requireBase: false,
      rewriteLinks: false,
    });
  },
]);

/* curalate.default and curalate.legacy are extensions of the curalate app
    - No components or services are defined in these apps directly
    - These apps are only for use in a `ng-app` or `curalate-async-app` directive
    If you need to update a page to use HTML5Mode settings, and it currently uses `ng-app=curalate`,
    you can simply replace it with one of these apps.
*/
angular.module('curalate.default', ['curalate']).config([
  '$locationProvider',
  function ($locationProvider) {
    $locationProvider.html5Mode({
      // Enable HTML5 mode so we can use the History API to change the active URL
      // without angular freaking out
      enabled: true,
      // Don't require a base, as this changes link behavior and such
      requireBase: false,
    });
  },
]);

angular.module('curalate.legacy', ['curalate']).config([
  '$locationProvider',
  function ($locationProvider) {
    $locationProvider.html5Mode({
      // Enable HTML5 mode so we can use the History API to change the active URL
      // without angular freaking out
      enabled: true,
      // Don't require a base, as this changes link behavior and such
      requireBase: false,
      // Don't rewriteLinks: This prevents angular's $locationProvider from processing
      // click events on anchors in the page. If you switch this to true, angular will try to load inline pages
      // which normally require a full navigation (i.e. page does not reload, no request to server)
      rewriteLinks: false,
    });
  },
]);

const appRootSelector = '[ng-app^=curalate],[async-session-app^=curalate]';

//
// Hooks Angular http requests to ensure that we are never mixing requests across clients. This can happen if the user has more
// than one tab opened, switches the client on one tab, then goes back to another tab and invokes an AJAX request.
//
curalate.factory('clientIdCheckInterceptor', [
  '$q',
  '$window',
  '$cookies',
  function ($q, $window, $cookies) {
    // cache the clientId cookie value at page load
    const pageLoadClientId = $cookies.get('crl8.clntid');

    return {
      request(config) {
        const currentClientId = $cookies.get('crl8.clntid');
        if (pageLoadClientId !== currentClientId) {
          // refresh page and abort the request
          $window.location.href = $window.location.href.split('#')[0];

          return $q.reject({
            data: null,
            status: 400,
            headers: (header) => config.headers[header],
            config,
            statusText:
              'clientIdCheckInterceptor: original clientId does not match current clientId',
            xhrStatus: 'error',
          });
        }

        return config;
      },
    };
  },
]);

// Bypass the sceDelegate blockers for interpolated CDN values. This allows
// us to interpolate strings that contain non-relative URLs without worrying
// about them being blocked. This only applies to our CDN.
curalate.config([
  '$sceDelegateProvider',
  function ($sceDelegateProvider) {
    $sceDelegateProvider.resourceUrlWhitelist([
      'self', // This is the default, and allows us to embed relative URLs
      `${CURALATE_ENV.STATIC_ASSET_URL}/**`,
    ]);
  },
]);

//
// Hooks Angular http requests to make sure that all API ajax requests are made with the correct clientId.
// This was an issue on HTML5 SPA pages like Hashtags > Instagram, where navigating to a specific hashtag would trigger a race condition
// where the AJAX request was sent out before the URL bar had updated to the correct clientId prarameter.
//
crl8.userSessionPromise.then((session) => {
  curalate.factory('pushCurrentClientIdInterceptor', () => ({
    request(config) {
      if (
        session.client.id &&
        config.url &&
        config.url.indexOf('/api/') === 0
      ) {
        config.params = config.params || {};
        config.params.clientId = session.client.id;
      }
      return config;
    },
  }));
});

curalate.config([
  '$animateProvider',
  'datepickerConfig',
  'datepickerPopupConfig',
  function ($animateProvider, datepickerConfig, datepickerPopupConfig) {
    // Configure AngularUI date-picker defaults
    datepickerConfig.showWeeks = false;
    datepickerConfig.showButtonBar = false;
    datepickerConfig.formatDay = 'd';
    datepickerPopupConfig.showWeeks = false;
    datepickerPopupConfig.showButtonBar = false;
    datepickerPopupConfig.formatDay = 'd';
    datepickerPopupConfig.toggleWeeksText = null;

    // Enable animations only on elements with a class name matching this filter.
    // This helps with performance on many elements that don't use animations, yet still have animate-able transitions.
    $animateProvider.classNameFilter(/angular-animate/);
  },
]);

crl8.sessionPromise.then((session) => {
  curalate.config([
    '$httpProvider',
    function ($httpProvider) {
      $httpProvider.interceptors.push('cognitoTokenInterceptor');
    },
  ]);
});

crl8.userSessionPromise.then(() => {
  curalate.config([
    '$httpProvider',
    function ($httpProvider) {
      $httpProvider.interceptors.push('clientIdCheckInterceptor');
      $httpProvider.interceptors.push('pushCurrentClientIdInterceptor');
      $httpProvider.interceptors.push('gatewayApiInterceptor');
    },
  ]);
});

curalate.factory('rx', () => Rx);

curalate.run([
  '$window',
  '$rootScope',
  function ($window, $rootScope) {
    // WORKAROUND: DEV-3624 (unable to properly style select boxes in firefox)
    $rootScope.browser = {
      isFirefox: /firefox/i.test($window.navigator.userAgent),
    };
  },
]);

//
// Customize toastr template
//
curalate.run([
  '$templateCache',
  function ($templateCache) {
    $templateCache.put(
      'directives/toast/toast.html',
      '<div class="toast-notification {{toastType}} {{toastClass}} toast-clickable clearfix" ng-click="tapToast()">' +
        '<div ng-if="toastType">' +
        '<div class="tn-icon" ng-switch="toastType">' +
        '<i class="fa fa-check fa-lg" ng-switch-when="toast-success"></i>' +
        '<i class="fa fa-warning fa-lg" ng-switch-when="toast-warning"></i>' +
        '<i class="fa fa-ban fa-lg" ng-switch-when="toast-error"></i>' +
        '<i class="fa fa-info fa-lg" ng-switch-when="toast-info"></i>' +
        '<i class="curalate-loading-spinner-wrapper" ng-switch-when="toast-loading"></i>' +
        '</div>' +
        '</div>' +
        '<div class="tn-message" ng-switch="allowHtml">' +
        '<div ng-switch-default ng-if="title" class="{{titleClass}}">{{title}}</div>' +
        '<div ng-switch-default class="{{messageClass}}">{{message}}</div>' +
        '<div ng-switch-when="true" ng-if="title" class="{{titleClass}}" ng-bind-html="title"></div>' +
        '<div ng-switch-when="true" class="{{messageClass}}" ng-bind-html="message"></div>' +
        '</div>' +
        '</div>',
    );
  },
]);

curalate.run([
  '$location',
  '$log',
  '$rootElement',
  '$rootScope',
  '$timeout',
  function ($location, $log, $rootElement, $rootScope) {
    if (!$rootElement.is(appRootSelector)) {
      // Skipping URL management code on a non-main app.
      return;
    }

    crl8.userSessionPromise.then((session) => {
      function updateSessionParams() {
        if (session.client.isClientIdOverrideActive) {
          $location.search('clientId', session.client.id);
        }
        if (session.dateRange.isOverrideActive) {
          const params = CURALATE.misc.getDateRangeParameters(
            session.dateRange,
          );
          $location.search('dateRange', params.dateRange);
          $location.search('fromDate', params.fromDate);
          $location.search('toDate', params.toDate);
        }
        $location.replace();
      }

      $rootScope.$on('$locationChangeSuccess', () => {
        // Re-add any override clientId
        // Running in a $timeout to avoid infdig on ui-router enabled pages
        updateSessionParams();
      });
    });
  },
]);

curalate.run([
  '$rootScope',
  'TrackingService',
  function ($rootScope, TrackingService) {
    $rootScope.$on('$locationChangeSuccess', (event, newUrl, oldUrl) => {
      const linkElem = document.createElement('a');

      linkElem.href = newUrl;
      const newPath = linkElem.pathname;

      linkElem.href = oldUrl;
      const oldPath = linkElem.pathname;

      if (newPath !== oldPath) {
        TrackingService.updatePage(newPath, true);
      }
    });
  },
]);

curalate.constant('RestBaseUrl', '/api');

uia.config([
  '$anchorScrollProvider',
  function ($anchorScrollProvider) {
    // OMG ANGULAR WHY DO YOU MAKE ME DO THESE THINGS
    $anchorScrollProvider.disableAutoScrolling();
  },
]);

curalate.constant('CURRENT_LOADER_OPTIONS', {
  INFINITE_SCROLL_LOADER: 'InfiniteScrollLoader',
  MAIN_LOADER: 'MainLoader',
  NO_LOADER: 'NoLoader',
});
